import { useEffect, useRef } from "react"
import type { MouseEvent } from "react"

import {
  ArrowPathIcon,
  CheckCircleIcon,
  LockClosedIcon,
  MinusIcon,
} from "@heroicons/react/20/solid"
import { clsx } from "clsx"
import { Link } from "react-router-dom"

import {
  type UpdateCopy,
  type UpdateFix,
  updatesBlockedBy,
  type UpdatesSpec,
  updatesUnknownNote,
} from "./proposal-updates"
import type { UpdatesRunner } from "./use-updates-runner"

/** Scrolls to the first of the update's sections the page renders. */
const scrollToSections =
  (sections: readonly string[]) =>
  (event: MouseEvent<HTMLAnchorElement>): void => {
    const target = sections
      .map((id) => document.getElementById(id))
      .find((element) => element !== null)
    if (!target) return
    event.preventDefault()
    target.scrollIntoView({ behavior: `smooth`, block: `start` })
  }

const actionButtonClass = `cursor-pointer rounded-full border border-bp-border bg-bp-glass-hi px-3 py-1 text-[length:var(--step--2)] font-semibold text-bp-text hover:bg-bp-elevated focus-visible:ring-2 focus-visible:ring-bp-accent focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-55`

/**
 * The seller's Updates panel for any Proposal kind: a bottom sheet on a phone,
 * a card on the right edge on desktop. The kind decides what is needed, what
 * its lock blocks and where a fix lives elsewhere; the panel shows the rows,
 * runs them and explains why one waits.
 */
export function UpdatesPanel<K extends string>({
  spec,
  intro,
  open,
  minimized,
  rows,
  needed,
  runner,
  staleLabel,
  actionLabel,
  fixElsewhere,
  locked,
  lock,
  lockUnavailable,
  onRetryLock,
  unknown,
  onRetryUnknown,
  onMinimize,
  onRestore,
}: {
  spec: UpdatesSpec<K>
  /** The line under the heading. */
  intro: string
  open: boolean
  /** Show the tab that reopens the panel. */
  minimized: boolean
  rows: readonly K[]
  needed: readonly K[]
  runner: UpdatesRunner<K>
  /** A needed row's state: never calculated, or out of date. */
  staleLabel: (key: K) => string
  /** A needed row's button, when its copy's action doesn't fit every state. */
  actionLabel?: (key: K) => string
  /** A needed row the panel can't run links here instead. */
  fixElsewhere: (key: K) => UpdateFix | null
  /** The kind's lock keeps this update from running now. */
  locked: (key: K) => boolean
  /** The lock that holds the updates, in the server's words. */
  lock: string | null
  /** Why the lock is unknown, when its check failed. */
  lockUnavailable: string | null
  onRetryLock: () => void
  /** Reads whose last check failed; a fix that builds on one waits. */
  unknown: readonly K[]
  onRetryUnknown: () => void
  onMinimize: () => void
  onRestore: () => void
}): JSX.Element {
  const panelId = `${spec.idPrefix}-updates-panel`
  const lockNoteId = `${spec.idPrefix}-updates-locked`
  const unknownNoteId = `${spec.idPrefix}-updates-unknown-note`
  const busy = runner.running !== null
  const updateDisabled = (key: K): boolean => busy || locked(key)
  const unknownSet = new Set(unknown)
  const count = needed.length
  const title = busy
    ? `Updating`
    : count === 0
      ? `Up to date`
      : count === 1
        ? `1 update needed`
        : `${count} updates needed`
  const showUpdateAll = count >= 2 || (busy && runner.target === `all`)
  const describedBy =
    [
      lock || lockUnavailable !== null ? lockNoteId : null,
      unknown.length > 0 ? unknownNoteId : null,
    ]
      .filter(Boolean)
      .join(` `) || undefined

  // Minimize hands focus to the tab, and the tab hands it back.
  const minimizeButton = useRef<HTMLButtonElement>(null)
  // A started action replaces its button, so focus moves to the heading.
  const heading = useRef<HTMLHeadingElement>(null)
  const run = (target: K | `all`): void => {
    runner.run(target)
    heading.current?.focus()
  }
  const tabButton = useRef<HTMLButtonElement>(null)
  const wasMinimized = useRef(minimized)
  useEffect(() => {
    if (minimized && !wasMinimized.current) tabButton.current?.focus()
    if (!minimized && wasMinimized.current && open) {
      minimizeButton.current?.focus()
    }
    wasMinimized.current = minimized
  }, [minimized, open])

  return (
    <>
      <aside
        id={panelId}
        aria-label="Updates needed"
        data-testid={panelId}
        data-open={open}
        className={clsx(
          `fixed z-30 overflow-hidden rounded-[18px] border border-bp-gold/40 bg-bp-surface/95 shadow-[0_18px_50px_rgba(0,0,0,0.5)] backdrop-blur-xl print:hidden`,
          // Phone: a bottom sheet above the Setup button. Desktop: a card on
          // the right edge.
          `inset-x-3 bottom-20 lg:inset-x-auto lg:top-48 lg:right-4 lg:bottom-auto lg:w-[312px]`,
          `duration-300 ease-out motion-reduce:transition-none`,
          // Visible at once on opening, so focus can land in it; hidden only
          // once it has slid away.
          open
            ? `visible translate-0 opacity-100 transition-[transform,opacity]`
            : `invisible translate-y-[calc(100%+40px)] opacity-0 transition-[transform,opacity,visibility] lg:translate-x-[calc(100%+40px)] lg:translate-y-0`,
        )}
      >
        <header className="flex items-center gap-2.5 border-b border-bp-border px-4 py-3.5">
          <span
            aria-hidden="true"
            className="grid size-[22px] shrink-0 place-items-center rounded-full bg-bp-gold text-[12px] font-bold text-bp-page"
          >
            {Math.max(count, busy ? 1 : 0)}
          </span>
          <h2
            ref={heading}
            tabIndex={-1}
            aria-live="polite"
            className="text-[length:var(--step--1)] font-semibold text-bp-text focus:outline-none"
          >
            {title}
          </h2>
          <button
            ref={minimizeButton}
            type="button"
            aria-expanded="true"
            aria-controls={panelId}
            onClick={onMinimize}
            className="ml-auto inline-flex cursor-pointer items-center gap-1 rounded-md px-1.5 py-0.5 text-[length:var(--step--2)] text-bp-text-muted hover:text-bp-text focus-visible:ring-2 focus-visible:ring-bp-accent focus-visible:outline-none"
          >
            <MinusIcon aria-hidden="true" className="size-4" />
            Minimize
          </button>
        </header>
        <p className="px-4 pt-3 pb-1 text-[length:var(--step--2)] text-bp-text-muted">
          {intro}
        </p>
        {unknown.length > 0 ? (
          <div
            role="alert"
            data-testid={`${spec.idPrefix}-updates-unknown`}
            className="mx-4 mt-2 flex items-center gap-2 text-[length:var(--step--2)] text-bp-text-muted"
          >
            <span id={unknownNoteId}>
              {updatesUnknownNote({ spec, keys: unknown })}
            </span>
            <button
              type="button"
              onClick={onRetryUnknown}
              className="shrink-0 cursor-pointer font-semibold whitespace-nowrap text-bp-accent underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:ring-bp-accent focus-visible:outline-none"
            >
              Try again
            </button>
          </div>
        ) : null}
        {lockUnavailable !== null ? (
          <div
            role="alert"
            data-testid={`${spec.idPrefix}-updates-lock-unavailable`}
            className="mx-4 mt-2 flex items-center gap-2 text-[length:var(--step--2)] text-bp-text-muted"
          >
            <span id={lockNoteId}>{lockUnavailable}</span>
            <button
              type="button"
              onClick={onRetryLock}
              className="shrink-0 cursor-pointer font-semibold whitespace-nowrap text-bp-accent underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:ring-bp-accent focus-visible:outline-none"
            >
              Try again
            </button>
          </div>
        ) : null}
        {lock ? (
          <p
            id={lockNoteId}
            role="note"
            data-testid={lockNoteId}
            className="mx-4 mt-2 flex items-start gap-2 text-[length:var(--step--2)] text-bp-text-muted"
          >
            <LockClosedIcon
              aria-hidden="true"
              className="mt-0.5 size-4 shrink-0"
            />
            {lock}
          </p>
        ) : null}
        <ul className="grid px-2 pt-1 pb-2">
          {rows.map((key) => (
            <UpdateRow
              key={key}
              idPrefix={spec.idPrefix}
              updateKey={key}
              copy={spec.copy[key]}
              sections={spec.sections[key]}
              runner={runner}
              needed={needed.includes(key)}
              staleLabel={staleLabel(key)}
              action={actionLabel?.(key) ?? spec.copy[key].action}
              fixElsewhere={fixElsewhere(key)}
              disabled={
                updateDisabled(key) ||
                updatesBlockedBy({ spec, key, unknown: unknownSet }).length > 0
              }
              describedBy={describedBy}
              onRun={run}
            />
          ))}
        </ul>
        {showUpdateAll ? (
          <footer className="grid gap-2 border-t border-bp-border px-4 pt-3 pb-4">
            <button
              type="button"
              disabled={needed.some(updateDisabled) || unknown.length > 0}
              aria-describedby={describedBy}
              onClick={() => run(`all`)}
              className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-full bg-bp-accent px-4 py-2 text-[length:var(--step--1)] font-semibold text-bp-on-accent hover:bg-bp-accent-hover focus-visible:ring-2 focus-visible:ring-bp-accent focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-55"
            >
              {runner.running ? (
                <>
                  <ArrowPathIcon
                    aria-hidden="true"
                    className="size-4 motion-safe:animate-spin"
                  />
                  {spec.copy[runner.running].busy}…
                </>
              ) : (
                `Update all`
              )}
            </button>
            <span className="text-[length:var(--step--2)] text-bp-text-dim">
              Runs{` `}
              {needed
                .map((key) => spec.copy[key].name.toLowerCase())
                .join(`, then `)}
              .
            </span>
          </footer>
        ) : null}
      </aside>
      {minimized ? (
        <button
          ref={tabButton}
          type="button"
          aria-expanded="false"
          aria-controls={panelId}
          aria-label={`Show ${count === 1 ? `1 update` : `${count} updates`} needed`}
          onClick={onRestore}
          data-testid={`${spec.idPrefix}-updates-tab`}
          className="fixed right-0 bottom-20 z-30 flex cursor-pointer items-center gap-2 rounded-l-xl border border-r-0 border-bp-gold/50 bg-bp-gold/15 py-2.5 pr-3 pl-3.5 text-[length:var(--step--2)] font-semibold text-bp-gold backdrop-blur-md hover:bg-bp-gold/25 focus-visible:ring-2 focus-visible:ring-bp-gold focus-visible:outline-none lg:top-56 lg:bottom-auto print:hidden"
        >
          <span aria-hidden="true" className="size-2 rounded-full bg-bp-gold" />
          {count === 1 ? `1 update` : `${count} updates`}
        </button>
      ) : null}
    </>
  )
}

function UpdateRow<K extends string>({
  idPrefix,
  updateKey,
  copy,
  sections,
  runner,
  needed,
  staleLabel,
  action,
  fixElsewhere,
  disabled,
  describedBy,
  onRun,
}: {
  idPrefix: string
  updateKey: K
  copy: UpdateCopy
  sections: readonly string[]
  runner: UpdatesRunner<K>
  needed: boolean
  staleLabel: string
  /** The row's button. */
  action: string
  /** Where a needed row the panel can't run is fixed instead. */
  fixElsewhere: UpdateFix | null
  disabled: boolean
  /** The notes that explain a disabled action: the lock, unknown reads. */
  describedBy: string | undefined
  onRun: (key: K) => void
}): JSX.Element {
  const nameId = `${idPrefix}-update-${updateKey}-name`
  const state =
    runner.running === updateKey ? `running` : needed ? `stale` : `done`
  const failure = runner.failure?.key === updateKey ? runner.failure : null

  return (
    <li
      data-testid={`${idPrefix}-update-${updateKey}`}
      data-state={state}
      className="grid gap-1.5 border-bp-border px-2 py-2.5 not-first:rounded-none not-first:border-t"
    >
      <div className="grid grid-cols-[10px_1fr_auto] items-center gap-3">
        {state === `done` ? (
          <CheckCircleIcon
            aria-hidden="true"
            className="size-2.5 text-bp-success"
          />
        ) : (
          <span
            aria-hidden="true"
            className={clsx(
              `size-2.5 rounded-full`,
              state === `running`
                ? `bg-bp-accent motion-safe:animate-pulse`
                : `bg-bp-gold`,
            )}
          />
        )}
        <a
          href={`#${sections[0]}`}
          onClick={scrollToSections(sections)}
          className="group min-w-0 focus-visible:ring-2 focus-visible:ring-bp-accent focus-visible:outline-none"
        >
          <span
            id={nameId}
            className="block text-[length:var(--step--1)] font-semibold text-bp-text group-hover:underline"
          >
            {copy.name}
          </span>
          <span
            className={clsx(
              `block text-[length:var(--step--2)]`,
              state === `running`
                ? `text-bp-accent`
                : state === `done`
                  ? `text-bp-success`
                  : `text-bp-gold`,
            )}
          >
            {state === `running`
              ? copy.busy
              : state === `done`
                ? `Current`
                : staleLabel}
          </span>
        </a>
        {state === `stale` && fixElsewhere !== null ? (
          <Link
            to={fixElsewhere.path}
            aria-describedby={nameId}
            className={actionButtonClass}
          >
            {fixElsewhere.label}
          </Link>
        ) : state === `stale` ? (
          <button
            type="button"
            disabled={disabled}
            aria-describedby={[nameId, describedBy].filter(Boolean).join(` `)}
            onClick={() => onRun(updateKey)}
            className={actionButtonClass}
          >
            {action}
          </button>
        ) : (
          <span />
        )}
      </div>
      {failure ? (
        <p
          role="alert"
          className="pl-[22px] text-[length:var(--step--2)] text-bp-danger"
        >
          {failure.message}
          {failure.fix ? (
            <>
              {` `}
              <Link
                to={failure.fix.path}
                className="font-semibold whitespace-nowrap text-bp-accent underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:ring-bp-accent focus-visible:outline-none"
              >
                {failure.fix.label}
              </Link>
            </>
          ) : null}
        </p>
      ) : null}
    </li>
  )
}
